import { useState, useEffect, useCallback, useMemo } from "react";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";

import DataTable from "examples/Tables/DataTable";

import { CircularProgress, IconButton, Tooltip } from "@mui/material";
import FlagLoader from "layouts/flagloader";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import Icon from "@mui/material/Icon";

import { getAdminLeaveRequests, approveLeaveRequest, rejectLeaveRequest } from "api/users";
import { getLeaveStatusInfo } from "layouts/leavetracker/helper";

// ✅ NEW hook
import { useDropdownOptions } from "layouts/interviewSelected/hooks/useDropdownOptions";
import UserFilters from "layouts/interviewSelected/components/useFilters";

function LeaveRequest() {
  const [leaveData, setLeaveData] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);

  /* 🔹 PAGINATION */
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 10,
    last_page: 1,
    total: 0,
  });

  const [filters, setFilters] = useState({
    branch_id: "",
    shift_id: "",
    department_id: "",
    designation_id: "",
    role: "",
    status: "all",
  });

  const [searchTerm, setSearchTerm] = useState("");

  /* ✅ NEW – useDropdownOptions returns lazy‑loaded options and fetch functions */
  const {
    options: dropdownOptions,
    loading: dropdownLoading,
    fetchBranches,
    fetchShifts,
    fetchDepartments,
    fetchDesignations,
    fetchRoles,
  } = useDropdownOptions();

  const [rejectDialog, setRejectDialog] = useState({
    open: false,
    leaveId: null,
    rejectReason: "",
  });

  const [approveDialog, setApproveDialog] = useState({
    open: false,
    leaveId: null,
    approveReason: "",
  });

  const [actionLoading, setActionLoading] = useState(null);

  /* ----------------------------
     FETCH DATA WITH PAGINATION
  ---------------------------- */
  const fetchLeaveRequests = useCallback(
    async (isInitial = false) => {
      try {
        isInitial ? setPageLoading(true) : setTableLoading(true);

        const params = {
          ...filters,
          q: searchTerm,
          page: pagination.current_page,
          per_page: pagination.per_page,
        };

        Object.keys(params).forEach((k) => {
          if (!params[k] || params[k] === "all") delete params[k];
        });

        const response = await getAdminLeaveRequests(params);

        const data = Array.isArray(response?.data) ? response.data : response?.data?.data || [];

        setLeaveData(data);

        if (response) {
          setPagination((p) => ({
            ...p,
            current_page: response.current_page,
            last_page: response.last_page,
            total: response.total,
            per_page: response.per_page,
          }));
        }
      } catch (err) {
        setError(err.message);
        setLeaveData([]);
      } finally {
        setPageLoading(false);
        setTableLoading(false);
      }
    },
    [filters, searchTerm, pagination.current_page, pagination.per_page]
  );

  useEffect(() => {
    fetchLeaveRequests(true);
  }, []);

  useEffect(() => {
    fetchLeaveRequests(false);
  }, [filters, searchTerm, pagination.current_page]);

  /* 🔹 FILTER CHANGE → PAGE RESET */
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((p) => ({ ...p, current_page: 1 }));
  };

  const handleApproveLeave = async (id, reason) => {
    try {
      setActionLoading(id);
      await approveLeaveRequest(id, reason);
      fetchLeaveRequests(false);
      setApproveDialog({ open: false });
    } finally {
      setActionLoading(null);
    }
  };

  const handleRejectLeave = async (id, reason) => {
    try {
      setActionLoading(id);
      await rejectLeaveRequest(id, reason);
      fetchLeaveRequests(false);
      setRejectDialog({ open: false });
    } finally {
      setActionLoading(null);
    }
  };

  const formatDate = (d) =>
    d
      ? new Date(d).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
      : "N/A";


  // 🔹 Reason formatter: 6 words ke baad cut + ...
  const formatReason = (text, maxWords = 6) => {
    if (!text) return "N/A";
    const words = text.split(" ");
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(" ") + "...";
  };


  const getStatusColor = (s) => (s === "approved" ? "success" : s === "pending" ? "info" : "error");

  /* ================= TABLE ================= */
  const columns = useMemo(
    () => [
      { Header: "Leave Type", accessor: "leave_type" },
      { Header: "Requester", accessor: "requester" },
      // { Header: "Approver", accessor: "approver" },
      { Header: "From Date", accessor: "from_date" },
      { Header: "To Date", accessor: "to_date" },
      { Header: "Days", accessor: "days" },
      { Header: "Reason", accessor: "reason" },
      { Header: "Status", accessor: "status" },
      { Header: "Applied On", accessor: "applied_on" },
      { Header: "Actions", accessor: "actions" },
    ],
    []
  );

  const getPaginationRange = () => {
    const total = pagination.last_page;
    const current = pagination.current_page;
    const limit = 5;

    let start = Math.max(1, current - Math.floor(limit / 2));
    let end = start + limit - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - limit + 1);
    }

    const pages = [];

    // Always show first
    if (start > 1) {
      pages.push(1);
      if (start > 2) pages.push("...");
    }

    // Middle range
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    // Always show last
    if (end < total) {
      if (end < total - 1) pages.push("...");
      pages.push(total);
    }

    return pages;
  };

  const rows = useMemo(() => {
    return leaveData.map((req) => ({
      leave_type: req.leave_policy?.leave_name || "N/A",
      // requester: req.user?.name || "N/A",
      requester: `${req.user?.name || ""} ${req.user?.last_name || ""}`.trim() || "N/A",
      // approver: req.approver ? `${req.approver.name} ${req.approver.last_name || ""}`.trim() : "N/A",
      from_date: formatDate(req.from_date),
      to_date: formatDate(req.to_date),
      days: req.days,
      //reason: req.reason || "N/A",
      reason: (
        <SoftTypography
          variant="caption"
          sx={{
            whiteSpace: "normal",
            wordBreak: "break-word",
            maxWidth: 250,   // table column width control
          }}
          title={req.reason || ""} // hover pe full text dikhega
        >
          {formatReason(req.reason, 6)}
        </SoftTypography>
      ),


      status: (() => {
        const statusInfo = getLeaveStatusInfo(req.status);
        return (
          <SoftBox display="flex" alignItems="center" gap={0.5}>
            <Icon
              sx={{
                fontSize: "1rem",
                color: (theme) => theme.palette[statusInfo.color].main,
              }}
            >
              {statusInfo.icon}
            </Icon>
            <SoftTypography
              variant="button"
              fontWeight="medium"
              color={statusInfo.color}
              sx={{ textTransform: "capitalize", fontSize: "0.8rem" }}
            >
              {statusInfo.text}
            </SoftTypography>
          </SoftBox>
        );
      })(),
      applied_on: formatDate(req.created_at),
      actions:
        req.status === "pending" && (req.permissions?.can_approve || req.permissions?.can_reject) ? (
          <SoftBox display="flex" gap={1}>
            {req.permissions?.can_approve && (
              <Tooltip title="Approve">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => setApproveDialog({ open: true, leaveId: req.id })}
                >
                  {actionLoading === req.id ? (
                    // <CircularProgress size={16} />
                    <FlagLoader />
                  ) : (
                    <CheckIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}
            {req.permissions?.can_reject && (
              <Tooltip title="Reject">
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => setRejectDialog({ open: true, leaveId: req.id })}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </SoftBox>
        ) : (
          "-"
        ),
    }));
  }, [leaveData, actionLoading]);

  if (pageLoading) {
    return (
      <SoftBox p={3} textAlign="center">
        {/* <CircularProgress /> */}
        <FlagLoader />
      </SoftBox>
    );
  }

  return (
    <Card>
      <SoftBox p={3}>
        {/* ✅ UPDATED FILTERS – with new props */}
        <UserFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          dropdownOptions={dropdownOptions}
          loading={dropdownLoading}
          fetchBranches={fetchBranches}
          fetchShifts={fetchShifts}
          fetchDepartments={fetchDepartments}
          fetchDesignations={fetchDesignations}
          fetchRoles={fetchRoles}
          showHistoryFilters={false} // Leave requests probably don't need history filters
        />

        <SoftTypography variant="h6" mt={1} mb={0} color="text">
          All Leave Requests ({pagination.total})
        </SoftTypography>

        {tableLoading ? (
          <SoftBox textAlign="center" py={4}>
            {/* <CircularProgress /> */}
            <FlagLoader />
          </SoftBox>
        ) : (
          <DataTable table={{ columns, rows }} entriesPerPage={false} canSearch={false} />
        )}

        {/* PAGINATION */}
        {pagination.last_page > 1 && (
          <SoftBox mt={2} display="flex" justifyContent="center">
            <SoftPagination variant="gradient" color="info">
              {/* {Array.from({ length: pagination.last_page }, (_, i) => (
                <SoftPagination
                  key={i}
                  item
                  active={pagination.current_page === i + 1}
                  onClick={() =>
                    setPagination((p) => ({
                      ...p,
                      current_page: i + 1,
                    }))
                  }
                >
                  {i + 1}
                </SoftPagination>
              ))} */}
              {/* <SoftPagination> */}

              {/* FIRST */}
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: 1,
                  }))
                  window.scrollTo({ top: 0, behavior: "smooth" });

                }}
              >
                «
              </SoftPagination>

              {/* PREV */}
              <SoftPagination
                item
                disabled={pagination.current_page === 1}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: p.current_page - 1,
                  }))
                  window.scrollTo({ top: 0, behavior: "smooth" });

                }}
              >
                ‹
              </SoftPagination>

              {/* PAGE NUMBERS */}
              {getPaginationRange().map((item, i) => {
                if (item === "...") {
                  return (
                    <SoftPagination key={i} item disabled>
                      ...
                    </SoftPagination>
                  );
                }

                return (
                  <SoftPagination
                    key={i}
                    item
                    active={pagination.current_page === item}
                    onClick={() => {
                      setPagination((p) => ({
                        ...p,
                        current_page: item,
                      }))
                      window.scrollTo({ top: 0, behavior: "smooth" });

                    }}
                  >
                    {item}
                  </SoftPagination>
                );
              })}

              {/* NEXT */}
              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: p.current_page + 1,
                  }))
                  window.scrollTo({ top: 0, behavior: "smooth" });

                }}
              >
                ›
              </SoftPagination>

              {/* LAST */}
              <SoftPagination
                item
                disabled={pagination.current_page === pagination.last_page}
                onClick={() => {
                  setPagination((p) => ({
                    ...p,
                    current_page: pagination.last_page,
                  }))
                  window.scrollTo({ top: 0, behavior: "smooth" });

                }}
              >
                »
              </SoftPagination>

            </SoftPagination>
            {/* </SoftPagination> */}
          </SoftBox>
        )}
      </SoftBox>

      {/* APPROVE DIALOG */}
      <Dialog open={approveDialog.open} onClose={() => setApproveDialog({ open: false })}>
        <DialogTitle>Approve Leave</DialogTitle>
        <DialogContent>
          <SoftInput
            multiline
            rows={3}
            value={approveDialog.approveReason}
            onChange={(e) =>
              setApproveDialog((p) => ({
                ...p,
                approveReason: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={() => setApproveDialog({ open: false })}>Cancel</SoftButton>
          <SoftButton
            color="success"
            onClick={() => handleApproveLeave(approveDialog.leaveId, approveDialog.approveReason)}
          >
            Approve
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* REJECT DIALOG */}
      <Dialog open={rejectDialog.open} onClose={() => setRejectDialog({ open: false })}>
        <DialogTitle>Reject Leave</DialogTitle>
        <DialogContent>
          <SoftInput
            multiline
            rows={3}
            value={rejectDialog.rejectReason}
            onChange={(e) =>
              setRejectDialog((p) => ({
                ...p,
                rejectReason: e.target.value,
              }))
            }
          />
        </DialogContent>
        <DialogActions>
          <SoftButton onClick={() => setRejectDialog({ open: false })}>Cancel</SoftButton>
          <SoftButton
            color="error"
            onClick={() => handleRejectLeave(rejectDialog.leaveId, rejectDialog.rejectReason)}
          >
            Submit
          </SoftButton>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

export default LeaveRequest;
